import React from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import { MacroIndicatorTypeLabels } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

const BondsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <EmptyState message={error} />;
    if (!data) return <EmptyState />;

    const {
        latest_yields = {},
        yields_history = {},
    } = data;

    const latestYieldKeys = Object.keys(latest_yields);
    const yieldsHistoryKeys = Object.keys(yields_history);

    return (
        <Box>
            {/* Latest Yields Cards */}
            <SectionTitle>Latest Yields</SectionTitle>
            {latestYieldKeys.length === 0 ? <EmptyState message="No latest yield data available" /> : (
                <Grid container spacing={2}>
                    {latestYieldKeys.map((key) => {
                        const item = latest_yields[key];
                        return (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {MacroIndicatorTypeLabels[key] || key}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {formatNumber(item?.value)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: item?.change_percent != null
                                                    ? (Number(item.change_percent) >= 0 ? 'success.main' : 'error.main')
                                                    : 'text.secondary',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {formatPercent(item?.change_percent)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Yields History */}
            <SectionTitle>Yields History</SectionTitle>
            {yieldsHistoryKeys.length === 0 ? <EmptyState message="No yields history data available" /> : (
                yieldsHistoryKeys.map((key) => {
                    const rows = yields_history[key] || [];
                    return (
                        <Box key={key} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                {MacroIndicatorTypeLabels[key] || key}
                            </Typography>
                            {rows.length === 0 ? <EmptyState message="No history records" /> : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Release Date</TableCell>
                                                <TableCell align="right">Value</TableCell>
                                                <TableCell align="right">Previous Value</TableCell>
                                                <TableCell align="right">Change %</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{formatDate(row.release_date)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.value)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.previous_value)}</TableCell>
                                                    <TableCell align="right">{formatPercent(row.change_percent)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    );
                })
            )}
        </Box>
    );
};

export default BondsTab;
